import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { closeModal, setModalType, openModal } from "../../redux/slices/uiSlice";
import { setOtpVerificationData, setEmailVerification } from "../../redux/slices/authSlice";
import { setUser } from "../../redux/slices/userSlice";
import type { RootState } from "../../redux/store";
import ModalWrapper from "./ModalWrapper";
import { REGEX } from "../../utils/constant";
import { registerWithEmailAPI, setAuthToken, getCountryListAPI, checkPhoneExistsAPI, checkPhoneExistsRegisterAPI, socialLoginAPI } from "../../api/auth";
import useToast from "../../hooks/useToast";
import useGoogleSocialLogin from "../../hooks/useGoogleSocialLogin";
import { useTranslation } from "react-i18next";
import { sendPhoneOtp, cleanupRecaptchaContainer, requestNotificationPermissionOnGesture } from "../../utils/firebaseConfig";
import { updateDeviceTokenAPI } from "../../api/notification";
import { decodeAppleToken, waitAppleID } from "../../utils/apple";
import { getFacebookLoginStatus, loadFacebookSDK } from "../../utils/facebook";
import { getCharacterListAPI } from "../../api/user";
import { FiCalendar } from "react-icons/fi";
import {
  getPhoneLengthErrorKey,
  getPhoneMaxLength,
  getSendPhoneOtpToastMessage,
  isFirebaseInvalidPhoneError,
  isValidPhoneLength,
  sanitizePhoneInput,
} from "../../utils/phoneValidation";
import { isInappropriateUsername } from "../../utils/usernameValidation";
// Country code to flag emoji mapping
const COUNTRY_FLAGS: Record<string, string> = {
  "973": "🇧🇭", // Bahrain
  "965": "🇰🇼", // Kuwait
  "968": "🇴🇲", // Oman
  "974": "🇶🇦", // Qatar
  "966": "🇸🇦", // Saudi Arabia
  "971": "🇦🇪", // UAE
};

const errorStyle = "text-red-500 lg:text-[0.729vw] text-sm lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold";

// Helper function to capitalize first letter of each word
const capitalizeWords = (str: string): string => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

type CharacterItem = {
  id: string;
  image: string;
  type: string;
  gender: "male" | "female";
};

const maxDobDate = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 13);
  d.setHours(23, 59, 59, 999);
  return d;
})();

const todayDobDate = (() => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
})();

const RegisterProfileFields: React.FC<{ formik: any; isPending: boolean }> = ({ formik, isPending }) => {
  const { t } = useTranslation();
  const { data: characterListData } = useQuery({
    queryKey: ["characterList"],
    queryFn: () => getCharacterListAPI({ type: "standing" }),
  });

  const standingCharacters: CharacterItem[] = useMemo(() => {
    const characters = (characterListData as any)?.data?.characters || [];
    return characters.filter((item: CharacterItem) => item.type === "standing");
  }, [characterListData]);

  const filteredStandingCharacters: CharacterItem[] = useMemo(() => {
    if (!formik.values.gender) return [];
    return standingCharacters.filter(
      (item: CharacterItem) =>
        (item.gender || "").toLowerCase() === (formik.values.gender || "").toLowerCase()
    );
  }, [standingCharacters, formik.values.gender]);

  useEffect(() => {
    if (!formik.values.profile_image || !formik.values.gender) return;
    const selectedCharacter = standingCharacters.find((item) => item.image === formik.values.profile_image);
    if (selectedCharacter && selectedCharacter.gender !== formik.values.gender) {
      formik.setFieldValue("profile_image", "");
    }
  }, [formik.values.gender, formik.values.profile_image, standingCharacters]);

  return (
    <>
      <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
        <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] transition-all ${formik.touched.dob && formik.errors.dob ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]" : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"}`}>
          <FiCalendar className="absolute rtl:right-4 rtl:left-auto left-4 top-1/2 -translate-y-1/2 text-[#110716] lg:text-[1.2vw] text-lg" />
          <input
            type="date"
            name="dob"
            value={formik.values.dob}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            max={maxDobDate.toISOString().split("T")[0]}
            disabled={isPending}
            className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full lg:ps-[8%] ps-10 lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent"
          />
        </div>
        {formik.touched.dob && formik.errors.dob && <p className={errorStyle}>{formik.errors.dob}</p>}
      </div>

      <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <label className={`flex items-center justify-center gap-2 rounded-[5px] border-[1.5px] py-2 font-bold transition-all ${formik.values.gender === "male" ? "border-primary-orange bg-[#FFE7DF]" : "border-black-300 bg-white"} ${isPending ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
            <input type="radio" name="gender" value="male" checked={formik.values.gender === "male"} onChange={formik.handleChange} disabled={isPending} className="hidden" />
            <span>{t("myAccount.male")}</span>
          </label>
          <label className={`flex items-center justify-center gap-2 rounded-[5px] border-[1.5px] py-2 font-bold transition-all ${formik.values.gender === "female" ? "border-primary-orange bg-[#FFE7DF]" : "border-black-300 bg-white"} ${isPending ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
            <input type="radio" name="gender" value="female" checked={formik.values.gender === "female"} onChange={formik.handleChange} disabled={isPending} className="hidden" />
            <span>{t("myAccount.female")}</span>
          </label>
        </div>
        {formik.touched.gender && formik.errors.gender && <p className={errorStyle}>{formik.errors.gender}</p>}
      </div>

      <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
        <div className="grid grid-cols-3 gap-2">
          {filteredStandingCharacters.map((character: CharacterItem) => (
            <button
              key={character.id}
              type="button"
              onClick={() => formik.setFieldValue("profile_image", character.image)}
              disabled={isPending}
              className={`rounded-[5px] border-[1.5px] p-2 transition-all ${formik.values.profile_image === character.image ? "border-primary-orange bg-[#FFE7DF]" : "border-black-300 bg-white"}`}
            >
              <img src={character.image} alt="Character option" className="w-full h-[70px] object-contain" />
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

// ==================== Mobile Register Form Component ====================
const MobileRegisterForm: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isCountryPositionReady, setIsCountryPositionReady] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const countryButtonRef = useRef<HTMLButtonElement>(null);

  // Get stored OTP verification data for pre-filling form when coming back from OTP screen
  const otpVerificationData = useSelector((state: RootState) => state.auth.otpVerification);

  // Fetch country list
  const { data: countryListData, isLoading: isCountryListLoading } = useQuery({
    queryKey: ["countryList"],
    queryFn: getCountryListAPI,
  });

  const countryList = countryListData?.data || [];

  // Calculate dropdown position and handle click outside
  useEffect(() => {
    if (isCountryDropdownOpen && countryButtonRef.current) {
      const buttonRect = countryButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom + (window.innerWidth >= 1024 ? 4 : 4),
        left: buttonRect.left,
        width: buttonRect.width,
      });
      // Mark position as ready after calculation
      setIsCountryPositionReady(true);
    } else {
      // Reset position ready state when dropdown is closed
      setIsCountryPositionReady(false);
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".country-dropdown-container") &&
        !target.closest(".country-dropdown-portal")
      ) {
        setIsCountryDropdownOpen(false);
        setCountrySearchQuery("");
      }
    };

    if (isCountryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Recalculate position on scroll/resize
      const handleResize = () => {
        if (countryButtonRef.current) {
          const buttonRect = countryButtonRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: buttonRect.bottom + (window.innerWidth >= 1024 ? 4 : 4),
            left: buttonRect.left,
            width: buttonRect.width,
          });
        }
      };
      window.addEventListener("scroll", handleResize, true);
      window.addEventListener("resize", handleResize);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleResize, true);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isCountryDropdownOpen]);

  const mobileValidationSchema = Yup.object({
    // countryCode: Yup.string().required(t("validation.countryCodeRequired")),
    mobileNumber: Yup.string()
      .required(t("validation.mobileRequired"))
      .matches(/^[0-9]+$/, t("validation.mobileValid"))
      .test("phone-length", t("validation.mobileValid"), function (value) {
        const { createError, path, parent } = this;
        if (!value) return true;
        if (!isValidPhoneLength(value, parent.countryCode)) {
          return createError({
            path,
            message: t(`validation.${getPhoneLengthErrorKey(parent.countryCode)}`),
          });
        }
        return true;
      }),
    full_name: Yup.string()
      .trim()
      .required(t("validation.nameRequired"))
      .matches(/^[a-zA-Z\u0600-\u06FF\s]+$/, t("validation.nameValid"))
      .test("not-only-spaces", t("validation.nameSpaces"), (value) => (value ? value.trim().length > 0 : false))
      .test("not-inappropriate", t("validation.nameInappropriate"), (value) => !value || !isInappropriateUsername(value))
      .min(2, t("validation.nameMin"))
      .max(20, t("validation.nameMax")),
    dob: Yup.date()
      .required(t("validation.dobRequired"))
      .typeError(t("validation.dobRequired"))
      .max(todayDobDate, t("validation.dobFuture"))
      .max(maxDobDate, t("validation.dobMinAge")),
    gender: Yup.string().required(t("validation.genderRequired")),
    acceptTerms: Yup.boolean().oneOf([true], t("validation.termsRequired")).required(t("validation.termsRequired")),
    // ageConfirmed: Yup.boolean().oneOf([true], t("validation.ageRequired")).required(t("validation.ageRequired")),
  });

  const formik = useFormik({
    initialValues: {
      countryCode: otpVerificationData?.flowType === "register" ? otpVerificationData?.countryCode || "973" : "973", // Default to Bahrain (+973)
      mobileNumber: otpVerificationData?.flowType === "register" ? otpVerificationData?.phoneNumber || "" : "",
      full_name: otpVerificationData?.flowType === "register" ? otpVerificationData?.fullName || "" : "",
      dob: otpVerificationData?.flowType === "register" ? otpVerificationData?.dob || "" : "",
      gender: otpVerificationData?.flowType === "register" ? otpVerificationData?.gender || "" : "",
      profile_image: otpVerificationData?.flowType === "register" ? otpVerificationData?.profileImage || "" : "",
      acceptTerms: otpVerificationData?.flowType === "register" ? true : false,
      // ageConfirmed: otpVerificationData?.flowType === "register" ? true : false,
    },
    validationSchema: mobileValidationSchema,
    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      // Safely request notification permission in user gesture without blocking register flow
      requestNotificationPermissionOnGesture().then(async (fcmToken) => {
        if (fcmToken) {
          const jwtToken = localStorage.getItem("token") || sessionStorage.getItem("token");
          if (jwtToken && jwtToken !== "undefined") {
            try {
              await updateDeviceTokenAPI(fcmToken, "web");
            } catch (err) {
              console.warn("FCM registration warning inside gesture handler:", err);
            }
          }
        }
      }).catch(err => console.log("Gesture FCM permission ignored:", err));

      try {
        setIsSendingOtp(true);

        const response = await checkPhoneExistsRegisterAPI({
          phone_number: values.mobileNumber,
          country_code: values.countryCode,
        });
        if (!response.status) {
          setFieldError("mobileNumber", response.message || t("validation.mobileValid"));
          setFieldTouched("mobileNumber", true, false);
          return;
        }

        const fullPhoneNumber = `+${values.countryCode}${values.mobileNumber}`;
        await sendPhoneOtp(fullPhoneNumber);

        useToast(t("messages.otpSentSuccess"), "success");
        dispatch(
          setOtpVerificationData({
            phoneNumber: values.mobileNumber,
            countryCode: values.countryCode,
            fullName: values.full_name.trim(),
            dob: values.dob,
            gender: values.gender,
            profileImage: values.profile_image,
            isNewUser: true,
            flowType: "register",
          })
        );
        dispatch(setModalType("otpVerify"));
      } catch (error: any) {
        const message = error instanceof Error ? error.message : "";
        if (isFirebaseInvalidPhoneError(error)) {
          setFieldError("mobileNumber", t("validation.mobileValid"));
          setFieldTouched("mobileNumber", true, false);
        } else if (/already exists|already registered|مستخدم|موجود/i.test(message)) {
          setFieldError("mobileNumber", message);
          setFieldTouched("mobileNumber", true, false);
        } else {
          useToast(getSendPhoneOtpToastMessage(error, t), "error");
        }
        // Reset global recaptcha to allow retry
        cleanupRecaptchaContainer();
      } finally {
        setIsSendingOtp(false);
      }
    },
  });

  const isPending = isSendingOtp;

  // Filter countries based on search query
  const filteredCountries = countryList.filter((country: { id: string; country_name: string; country_code: string }) => {
    const searchLower = countrySearchQuery.toLowerCase();
    return (
      country.country_name.toLowerCase().includes(searchLower) ||
      country.country_code.includes(countrySearchQuery)
    );
  });

  const handleCountrySelect = (countryCode: string) => {
    const truncatedPhone = sanitizePhoneInput(formik.values.mobileNumber, countryCode);
    formik.setFieldValue("countryCode", countryCode);
    formik.setFieldValue("mobileNumber", truncatedPhone, true);
    setIsCountryDropdownOpen(false);
    setCountrySearchQuery("");
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* Mobile Number with Country Code (dropdown inside input) */}
      <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
        <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex overflow-visible
    2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px]
    transition-all
    ${formik.touched.mobileNumber && formik.errors.mobileNumber
            ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
            : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
          }
  `}>
          {/* Mobile Number Input */}
          <input
            type="tel"
            maxLength={getPhoneMaxLength(formik.values.countryCode)}
            name="mobileNumber"
            placeholder={t("register.mobileNumber")}
            value={formik.values.mobileNumber}
            onChange={(e) => {
              formik.setFieldValue(
                "mobileNumber",
                sanitizePhoneInput(e.target.value, formik.values.countryCode)
              );
            }}
            onBlur={formik.handleBlur}
            disabled={isPending || isCountryListLoading}
            className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full lg:ps-[7.6vw] md:ps-[135px] ps-[125px] rtl:2xl:pe-[5vw] rtl:md:pe-[100px] rtl:pe-[100px] lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent`}
          />

          {/* Country Code Dropdown positioned inside input */}
          <div className="absolute inset-y-0 start-0 flex items-center">
            <div className="country-dropdown-container relative 2xl:ms-[0.156vw] ms-[3px] lg:min-w-[6vw] min-w-[90px]">
              <button
                ref={countryButtonRef}
                type="button"
                onClick={() => {
                  if (!isPending && !isCountryListLoading) {
                    setIsCountryDropdownOpen(!isCountryDropdownOpen);
                    if (!isCountryDropdownOpen) {
                      setCountrySearchQuery("");
                    }
                  }
                }}
                disabled={isPending || isCountryListLoading}
                className={`rtl:text-right w-full h-full lg:py-[0.55vw] py-2 lg:px-[0.8vw] px-2 pe-7 2xl:rounded-[0.26vw] rounded-[5px] lg:text-[0.95vw]/[1.2vw] md:text-sm/tight text-xs/tight font-bold bg-primary-ivory hover:bg-primary-yellow focus:bg-primary-yellow border border-black-300 text-black-300 focus:outline-none transition-all disabled:opacity-50 cursor-pointer flex items-center justify-between  ${formik.touched.countryCode && formik.errors.countryCode ? "!border-red-500" : ""
                  }`}
              >
                <span className="truncate">+{formik.values.countryCode}</span>
                <svg
                  className={`lg:w-[0.8vw] w-3 lg:h-[0.8vw] h-3 text-black-400 transition-transform ${isCountryDropdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu - Rendered via Portal */}
              {isCountryDropdownOpen &&
                isCountryPositionReady &&
                typeof document !== "undefined" &&
                createPortal(
                  <div
                    className="country-dropdown-portal fixed z-[9999] lg:w-[20vw] w-[280px] 2xl:max-h-[20vw] max-h-[300px] bg-primary-ivory border border-black-300 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] overflow-hidden"
                    style={{
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                    }}
                  >
                    {/* Search Input */}
                    <div className="2xl:p-[0.5vw] p-2 border-b border-black-300">
                      <input
                        type="text"
                        placeholder={t("login.searchCountry") || "Search country..."}
                        value={countrySearchQuery}
                        onChange={(e) => setCountrySearchQuery(e.target.value)}
                        className="w-full lg:py-[0.4vw] py-1.5 lg:px-[0.6vw] px-2 2xl:rounded-[0.26vw] rounded-[5px] lg:text-[0.9vw]/[1.1vw] md:text-sm/tight text-xs/tight font-bold border border-black-300 text-black-300 focus:outline-none focus:border-primary-orange transition-all"
                        autoFocus
                      />
                    </div>

                    {/* Country List */}
                    <div className="overflow-y-auto 2xl:max-h-[calc(20vw-4vw)] max-h-[240px]">
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country: { id: string; country_name: string; country_code: string }) => (
                          <button
                            key={country.id}
                            type="button"
                            onClick={() => handleCountrySelect(country.country_code)}
                            className={`w-full text-start lg:py-[0.5vw] py-2 lg:px-[0.8vw] px-3 lg:text-[0.9vw]/[1.1vw] md:text-sm/tight text-xs/tight font-bold hover:bg-primary-yellow transition-colors flex items-center justify-between ${formik.values.countryCode === country.country_code ? "bg-primary-yellow" : ""
                              }`}
                          >
                            <span className="flex-1 truncate">{country.country_name}</span>
                            <span className="lg:ms-[0.5vw] ms-2 text-black-400">+{country.country_code}</span>
                          </button>
                        ))
                      ) : (
                        <div className="lg:py-[0.8vw] py-3 lg:px-[0.8vw] px-3 lg:text-[0.9vw]/[1.1vw] md:text-sm/tight text-xs/tight text-black-400 text-center">
                          {t("login.noCountryFound") || "No country found"}
                        </div>
                      )}
                    </div>
                  </div>,
                  document.body
                )}
            </div>
          </div>
        </div>

        {formik.touched.countryCode && formik.errors.countryCode && (
          <p className={errorStyle}>
            {formik.errors.countryCode}
          </p>
        )}
        {formik.touched.mobileNumber && formik.errors.mobileNumber && (
          <p className={errorStyle}>
            {formik.errors.mobileNumber}
          </p>
        )}
      </div>

      {/* Full Name Input */}
      <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
        <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
    2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px]
    transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]
  `}>
          <input
            type="text"
            name="full_name"
            placeholder={t("register.fullName")}
            value={formik.values.full_name}
            onChange={(e) => {
              const capitalizedValue = capitalizeWords(e.target.value);
              formik.setFieldValue("full_name", capitalizedValue);
            }}
            onBlur={formik.handleBlur}
            disabled={isPending}
            className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent ${formik.touched.full_name && formik.errors.full_name ? "!border-red-500" : ""
              }`}
          />
        </div>
        {formik.touched.full_name && formik.errors.full_name && (
          <p className={errorStyle}>
            {formik.errors.full_name}
          </p>
        )}
      </div>

      <RegisterProfileFields formik={formik} isPending={isPending} />

      {/* Terms & Conditions */}
      <div className="flex flex-col gap-2 lg:mb-[0.9vw] md:mb-5 mb-3">
        <label className="flex items-start lg:gap-[0.6vw] gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="acceptTerms"
            checked={formik.values.acceptTerms}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isPending}
            className="hidden"
          />
          <span className="-mt-0.5">
            {formik.values.acceptTerms ? (
              <svg className="2xl:w-[1.3vw] w-5 2xl:min-w-[1.3vw] min-w-5 2xl:h-[1.3vw] h-5" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 0.1875H21C22.5533 0.1875 23.8125 1.4467 23.8125 3V21C23.8125 22.5533 22.5533 23.8125 21 23.8125H3C1.4467 23.8125 0.1875 22.5533 0.1875 21V3C0.1875 1.4467 1.4467 0.1875 3 0.1875Z" fill="#110706" stroke="#110706" strokeWidth="0.375" />
                <path d="M1.3335 3.58301C1.3335 2.34037 2.34086 1.33301 3.5835 1.33301H20.4168C21.6595 1.33301 22.6668 2.34037 22.6668 3.58301V20.4163C22.6668 21.659 21.6595 22.6663 20.4168 22.6663H3.5835C2.34086 22.6663 1.3335 21.659 1.3335 20.4163V3.58301Z" fill="#D76A4D" />
                <path d="M10.4391 17.1281C10.3118 17.26 10.1382 17.3337 9.95778 17.3337C9.77737 17.3337 9.60377 17.26 9.47646 17.1281L5.6327 13.1673C5.23376 12.7563 5.23376 12.0899 5.6327 11.6796L6.11403 11.1837C6.51297 10.7727 7.15904 10.7727 7.55799 11.1837L9.95778 13.6562L16.4423 6.97526C16.8413 6.56424 17.488 6.56424 17.8863 6.97526L18.3676 7.47116C18.7666 7.88218 18.7666 8.54852 18.3676 8.95884L10.4391 17.1281Z" fill="#FFFBA1" />
              </svg>
            ) : (
              <svg className="2xl:w-[1.3vw] w-5 2xl:min-w-[1.3vw] min-w-5 2xl:h-[1.3vw] h-5" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 0.1875H21C22.5533 0.1875 23.8125 1.4467 23.8125 3V21C23.8125 22.5533 22.5533 23.8125 21 23.8125H3C1.4467 23.8125 0.1875 22.5533 0.1875 21V3C0.1875 1.4467 1.4467 0.1875 3 0.1875Z" fill="#110706" stroke="#110706" strokeWidth="0.375" />
                <path d="M1.3335 3.58301C1.3335 2.34037 2.34086 1.33301 3.5835 1.33301H20.4168C21.6595 1.33301 22.6668 2.34037 22.6668 3.58301V20.4163C22.6668 21.659 21.6595 22.6663 20.4168 22.6663H3.5835C2.34086 22.6663 1.3335 21.659 1.3335 20.4163V3.58301Z" fill="white" />
              </svg>
            )}
          </span>
          <span className="lg:text-[1.1vw]/[1.3vw] text-base/tight font-bold text-black-800">
            {t("register.iHaveRead")}{" "}
            <a href="/terms-conditions" target="_blank" className="text-primary-orange hover:underline">
              {t("register.termsAndConditions")}
            </a>{" "}
            {t("register.and")}{" "}
            <a href="/privacy-policy" target="_blank" className="text-primary-orange hover:underline">
              {t("register.privacyPolicy")}
            </a>
            .
          </span>
        </label>
        {formik.touched.acceptTerms && formik.errors.acceptTerms && (
          <p className={errorStyle}>{formik.errors.acceptTerms}</p>
        )}

        {/* Age Confirmation */}
        {/* <label className="flex items-start lg:gap-[0.6vw] gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="ageConfirmed"
            checked={formik.values.ageConfirmed}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isPending}
            className="hidden"
          />
          <span className="-mt-0.5">
            {formik.values.ageConfirmed ? (
              <svg className="2xl:w-[1.3vw] w-5 2xl:min-w-[1.3vw] min-w-5 2xl:h-[1.3vw] h-5" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 0.1875H21C22.5533 0.1875 23.8125 1.4467 23.8125 3V21C23.8125 22.5533 22.5533 23.8125 21 23.8125H3C1.4467 23.8125 0.1875 22.5533 0.1875 21V3C0.1875 1.4467 1.4467 0.1875 3 0.1875Z" fill="#110706" stroke="#110706" strokeWidth="0.375" />
                <path d="M1.3335 3.58301C1.3335 2.34037 2.34086 1.33301 3.5835 1.33301H20.4168C21.6595 1.33301 22.6668 2.34037 22.6668 3.58301V20.4163C22.6668 21.659 21.6595 22.6663 20.4168 22.6663H3.5835C2.34086 22.6663 1.3335 21.659 1.3335 20.4163V3.58301Z" fill="#D76A4D" />
                <path d="M10.4391 17.1281C10.3118 17.26 10.1382 17.3337 9.95778 17.3337C9.77737 17.3337 9.60377 17.26 9.47646 17.1281L5.6327 13.1673C5.23376 12.7563 5.23376 12.0899 5.6327 11.6796L6.11403 11.1837C6.51297 10.7727 7.15904 10.7727 7.55799 11.1837L9.95778 13.6562L16.4423 6.97526C16.8413 6.56424 17.488 6.56424 17.8863 6.97526L18.3676 7.47116C18.7666 7.88218 18.7666 8.54852 18.3676 8.95884L10.4391 17.1281Z" fill="#FFFBA1" />
              </svg>
            ) : (
              <svg className="2xl:w-[1.3vw] w-5 2xl:min-w-[1.3vw] min-w-5 2xl:h-[1.3vw] h-5" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 0.1875H21C22.5533 0.1875 23.8125 1.4467 23.8125 3V21C23.8125 22.5533 22.5533 23.8125 21 23.8125H3C1.4467 23.8125 0.1875 22.5533 0.1875 21V3C0.1875 1.4467 1.4467 0.1875 3 0.1875Z" fill="#110706" stroke="#110706" strokeWidth="0.375" />
                <path d="M1.3335 3.58301C1.3335 2.34037 2.34086 1.33301 3.5835 1.33301H20.4168C21.6595 1.33301 22.6668 2.34037 22.6668 3.58301V20.4163C22.6668 21.659 21.6595 22.6663 20.4168 22.6663H3.5835C2.34086 22.6663 1.3335 21.659 1.3335 20.4163V3.58301Z" fill="white" />
              </svg>
            )}
          </span>
          <span className="lg:text-[1.1vw]/[1.3vw] text-base/tight font-bold text-black-800">
            {t("register.above18")}
          </span>
        </label>
        {formik.touched.ageConfirmed && formik.errors.ageConfirmed && (
          <p className={errorStyle}>{formik.errors.ageConfirmed}</p>
        )} */}

        {/* Get OTP Button */}
        <button
          type="submit"
          disabled={isPending}
          className="lg:mb-[1.1vw] md:mb-5 mb-4 w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
        >
          <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full"><span className="skew-x-[10deg] block">{isPending ? t("register.sendingOtp") : t("register.getOtp")}</span></span>
        </button>
      </div>
    </form>
  );
};

// ==================== Email Register Form Component ====================
const EmailRegisterForm: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const emailValidationSchema = Yup.object({
    full_name: Yup.string()
      .trim()
      .required(t("validation.nameRequired"))
      .matches(/^[a-zA-Z\u0600-\u06FF\s]+$/, t("validation.nameValid"))
      .test("not-only-spaces", t("validation.nameSpaces"), (value) => (value ? value.trim().length > 0 : false))
      .test("not-inappropriate", t("validation.nameInappropriate"), (value) => !value || !isInappropriateUsername(value))
      .min(2, t("validation.nameMin"))
      .max(20, t("validation.nameMax")),
    dob: Yup.date()
      .required(t("validation.dobRequired"))
      .typeError(t("validation.dobRequired"))
      .max(todayDobDate, t("validation.dobFuture"))
      .max(maxDobDate, t("validation.dobMinAge")),
    gender: Yup.string().required(t("validation.genderRequired")),
    email: Yup.string().matches(REGEX.EMAIL, t("validation.emailValid")).required(t("validation.emailRequired")),
    password: Yup.string()
      .required(t("validation.passwordRequired"))
      .min(8, t("validation.passwordMin"))
      .matches(/^\S*$/, t("validation.passwordNoSpaces"))
      .matches(/[A-Z]/, t("validation.passwordUppercase"))
      .matches(/[a-z]/, t("validation.passwordLowercase"))
      .matches(/[0-9]/, t("validation.passwordNumber"))
      .matches(/[!@#$%^&*(),.?":{}|<>]/, t("validation.passwordSpecial")),
    acceptTerms: Yup.boolean().oneOf([true], t("validation.termsRequired")).required(t("validation.termsRequired")),
    // ageConfirmed: Yup.boolean().oneOf([true], t("validation.ageRequired")).required(t("validation.ageRequired")),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: registerWithEmailAPI,
    onSuccess: (data) => {

      const token = data?.data?.token;
      const user = data?.data?.user;
      const isEmailVerified = user?.is_email_verified;

      if (token) {
        // Store token in localStorage
        localStorage.setItem("token", token);
        setAuthToken(token);
      }

      if (user) {
        // Store user in Redux
        dispatch(setUser(user));
      }

      // Try to register fcm token if available after token is saved
      const fcmToken = localStorage.getItem("fcmToken");
      if (fcmToken) {
        updateDeviceTokenAPI(fcmToken, "web").catch(err => console.log("FCM registration error after login:", err));
      }

      // Check if email needs verification
      if (isEmailVerified === false && user?.email) {
        // Store email for verification modal
        dispatch(setEmailVerification(user.email));
        // Open verify email modal
        dispatch(openModal("verifyEmail"));
        useToast(t("messages.registrationSuccess"), "success");
      } else {
        // Email is verified or user is fully registered
        useToast(t("messages.registrationSuccess"), "success");
        dispatch(closeModal());

        // Navigate to create-game page
        setTimeout(() => {
          navigate("/my-account");
        }, 100);
      }
    },
    onError: (error: Error) => {
      console.error("Registration Error:", error.message);
      useToast(error.message || t("messages.registrationFailed"), "error");
    },
  });

  const formik = useFormik({
    initialValues: {
      full_name: "",
      dob: "",
      gender: "",
      profile_image: "",
      email: "",
      password: "",
      acceptTerms: false,
      // ageConfirmed: false,
    },
    validationSchema: emailValidationSchema,
    onSubmit: (values) => {
      // Safely request notification permission in user gesture without blocking submit
      requestNotificationPermissionOnGesture().then(async (fcmToken) => {
        if (fcmToken) {
          const jwtToken = localStorage.getItem("token") || sessionStorage.getItem("token");
          if (jwtToken && jwtToken !== "undefined") {
            try {
              await updateDeviceTokenAPI(fcmToken, "web");
            } catch (err) {
              console.warn("FCM registration warning inside gesture handler:", err);
            }
          }
        }
      }).catch(err => console.log("Gesture FCM permission ignored:", err));

      mutate({
        email: values.email,
        password: values.password,
        full_name: values.full_name.trim(),
        dob: values.dob,
        gender: values.gender,
        profile_image: values.profile_image,
      });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
        <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
    2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px]
    transition-all
    ${formik.touched.full_name && formik.errors.full_name
            ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
            : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
          }
  `}>
          <input
            type="text"
            name="full_name"
            placeholder={t("register.fullName")}
            value={formik.values.full_name}
            onChange={(e) => {
              const capitalizedValue = capitalizeWords(e.target.value);
              formik.setFieldValue("full_name", capitalizedValue);
            }}
            onBlur={formik.handleBlur}
            disabled={isPending}
            className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent`}
          />
        </div>
        {formik.touched.full_name && formik.errors.full_name && (
          <p className={errorStyle}>
            {formik.errors.full_name}
          </p>
        )}

      </div>

      <RegisterProfileFields formik={formik} isPending={isPending} />

      {/* Email Input */}
      <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
        <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
    2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px]
    transition-all
    ${formik.touched.email && formik.errors.email
            ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
            : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
          }
  `}>
          <input
            type="text"
            name="email"
            placeholder={t("register.emailAddress")}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isPending}
            className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent`}
          />
        </div>
        {formik.touched.email && formik.errors.email && (
          <p className={errorStyle}>
            {formik.errors.email}
          </p>
        )}

      </div>

      {/* Password Input */}
      <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
        <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
    2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px]
    transition-all
    ${formik.touched.password && formik.errors.password
            ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
            : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
          }
  `}>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder={t("register.password")}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isPending}
            className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute lg:end-[1vw] end-5 top-1/2 -translate-y-1/2 text-black-400 hover:text-black-300 transition-colors"
          >
            {showPassword ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="lg:w-[1.042vw] lg:h-[1.042vw] w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="lg:w-[1.042vw] lg:h-[1.042vw] w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        </div>
        {formik.touched.password && formik.errors.password && (
          <p className={errorStyle}>
            {formik.errors.password}
          </p>
        )}
      </div>

      {/* Terms & Conditions */}
      <div className="flex flex-col gap-2 lg:mb-[0.9vw] md:mb-5 mb-3">
        <label className="flex items-start lg:gap-[0.6vw] gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="acceptTerms"
            checked={formik.values.acceptTerms}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isPending}
            className="hidden"
          />
          <span className="-mt-0.5">
            {formik.values.acceptTerms ? (
              <svg className="2xl:w-[1.3vw] w-5 2xl:min-w-[1.3vw] min-w-5 2xl:h-[1.3vw] h-5" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 0.1875H21C22.5533 0.1875 23.8125 1.4467 23.8125 3V21C23.8125 22.5533 22.5533 23.8125 21 23.8125H3C1.4467 23.8125 0.1875 22.5533 0.1875 21V3C0.1875 1.4467 1.4467 0.1875 3 0.1875Z" fill="#110706" stroke="#110706" strokeWidth="0.375" />
                <path d="M1.3335 3.58301C1.3335 2.34037 2.34086 1.33301 3.5835 1.33301H20.4168C21.6595 1.33301 22.6668 2.34037 22.6668 3.58301V20.4163C22.6668 21.659 21.6595 22.6663 20.4168 22.6663H3.5835C2.34086 22.6663 1.3335 21.659 1.3335 20.4163V3.58301Z" fill="#D76A4D" />
                <path d="M10.4391 17.1281C10.3118 17.26 10.1382 17.3337 9.95778 17.3337C9.77737 17.3337 9.60377 17.26 9.47646 17.1281L5.6327 13.1673C5.23376 12.7563 5.23376 12.0899 5.6327 11.6796L6.11403 11.1837C6.51297 10.7727 7.15904 10.7727 7.55799 11.1837L9.95778 13.6562L16.4423 6.97526C16.8413 6.56424 17.488 6.56424 17.8863 6.97526L18.3676 7.47116C18.7666 7.88218 18.7666 8.54852 18.3676 8.95884L10.4391 17.1281Z" fill="#FFFBA1" />
              </svg>
            ) : (
              <svg className="2xl:w-[1.3vw] w-5 2xl:min-w-[1.3vw] min-w-5 2xl:h-[1.3vw] h-5" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 0.1875H21C22.5533 0.1875 23.8125 1.4467 23.8125 3V21C23.8125 22.5533 22.5533 23.8125 21 23.8125H3C1.4467 23.8125 0.1875 22.5533 0.1875 21V3C0.1875 1.4467 1.4467 0.1875 3 0.1875Z" fill="#110706" stroke="#110706" strokeWidth="0.375" />
                <path d="M1.3335 3.58301C1.3335 2.34037 2.34086 1.33301 3.5835 1.33301H20.4168C21.6595 1.33301 22.6668 2.34037 22.6668 3.58301V20.4163C22.6668 21.659 21.6595 22.6663 20.4168 22.6663H3.5835C2.34086 22.6663 1.3335 21.659 1.3335 20.4163V3.58301Z" fill="white" />
              </svg>
            )}
          </span>
          <span className="lg:text-[1.1vw]/[1.3vw] text-base/tight font-bold text-black-800">
            {t("register.iHaveRead")}{" "}
            <a href="/terms-conditions" target="_blank" className="text-primary-orange hover:underline">
              {t("register.termsAndConditions")}
            </a>{" "}
            {t("register.and")}{" "}
            <a href="/privacy-policy" target="_blank" className="text-primary-orange hover:underline">
              {t("register.privacyPolicy")}
            </a>
            .
          </span>
        </label>
        {formik.touched.acceptTerms && formik.errors.acceptTerms && (
          <p className={errorStyle}>{formik.errors.acceptTerms}</p>
        )}

        {/* Age Confirmation */}
        {/* <label className="flex items-start lg:gap-[0.6vw] gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="ageConfirmed"
            checked={formik.values.ageConfirmed}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isPending}
            className="hidden"
          />
          <span className="-mt-0.5">
            {formik.values.ageConfirmed ? (
              <svg className="2xl:w-[1.3vw] w-5 2xl:min-w-[1.3vw] min-w-5 2xl:h-[1.3vw] h-5" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 0.1875H21C22.5533 0.1875 23.8125 1.4467 23.8125 3V21C23.8125 22.5533 22.5533 23.8125 21 23.8125H3C1.4467 23.8125 0.1875 22.5533 0.1875 21V3C0.1875 1.4467 1.4467 0.1875 3 0.1875Z" fill="#110706" stroke="#110706" strokeWidth="0.375" />
                <path d="M1.3335 3.58301C1.3335 2.34037 2.34086 1.33301 3.5835 1.33301H20.4168C21.6595 1.33301 22.6668 2.34037 22.6668 3.58301V20.4163C22.6668 21.659 21.6595 22.6663 20.4168 22.6663H3.5835C2.34086 22.6663 1.3335 21.659 1.3335 20.4163V3.58301Z" fill="#D76A4D" />
                <path d="M10.4391 17.1281C10.3118 17.26 10.1382 17.3337 9.95778 17.3337C9.77737 17.3337 9.60377 17.26 9.47646 17.1281L5.6327 13.1673C5.23376 12.7563 5.23376 12.0899 5.6327 11.6796L6.11403 11.1837C6.51297 10.7727 7.15904 10.7727 7.55799 11.1837L9.95778 13.6562L16.4423 6.97526C16.8413 6.56424 17.488 6.56424 17.8863 6.97526L18.3676 7.47116C18.7666 7.88218 18.7666 8.54852 18.3676 8.95884L10.4391 17.1281Z" fill="#FFFBA1" />
              </svg>
            ) : (
              <svg className="2xl:w-[1.3vw] w-5 2xl:min-w-[1.3vw] min-w-5 2xl:h-[1.3vw] h-5" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 0.1875H21C22.5533 0.1875 23.8125 1.4467 23.8125 3V21C23.8125 22.5533 22.5533 23.8125 21 23.8125H3C1.4467 23.8125 0.1875 22.5533 0.1875 21V3C0.1875 1.4467 1.4467 0.1875 3 0.1875Z" fill="#110706" stroke="#110706" strokeWidth="0.375" />
                <path d="M1.3335 3.58301C1.3335 2.34037 2.34086 1.33301 3.5835 1.33301H20.4168C21.6595 1.33301 22.6668 2.34037 22.6668 3.58301V20.4163C22.6668 21.659 21.6595 22.6663 20.4168 22.6663H3.5835C2.34086 22.6663 1.3335 21.659 1.3335 20.4163V3.58301Z" fill="white" />
              </svg>
            )}
          </span>
          <span className="lg:text-[1.1vw]/[1.3vw] text-base/tight font-bold text-black-800">
            {t("register.above18")}
          </span>
        </label>
        {formik.touched.ageConfirmed && formik.errors.ageConfirmed && (
          <p className={errorStyle}>{formik.errors.ageConfirmed}</p>
        )} */}

        {/* Signup Button */}
        <button
          type="submit"
          disabled={isPending}
          className="lg:mb-[1.1vw] md:mb-5 mb-4 w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
        >
          <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full"><span className="skew-x-[10deg] block"> {isPending ? t("register.signingUp") : t("register.signUp")}</span></span>
        </button>
      </div>

    </form>
  );
};

// ==================== Main Register Modal Component ====================
const RegisterModal: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [authMode, setAuthMode] = useState<"mobile" | "email">("mobile");
  const navigate = useNavigate();
  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleCommingSoon = () => {
    useToast("Coming soon", "info");
    return;
  };

  // Use the reusable Google Social Login hook
  const { googleLogin, isLoading: isGoogleLoading } = useGoogleSocialLogin();

  const handleGoogleLogin = () => {
    requestNotificationPermissionOnGesture();
    googleLogin();
  };



  useEffect(() => {
    const initApple = async () => {
      try {
        await waitAppleID();
        const apple = (window as any).AppleID;
        apple.auth.init({
          clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
          scope: "name email",
          redirectURI: import.meta.env.VITE_APPLE_REDIRECT_URI,
          usePopup: true,
        });
      } catch (error) {
        console.error("Apple SDK initialization failed", error);
      }
    };

    initApple();

    document.addEventListener("AppleIDSignInOnSuccess", onAppleSuccess);
    document.addEventListener("AppleIDSignInOnFailure", onAppleFailure);

    return () => {
      document.removeEventListener("AppleIDSignInOnSuccess", onAppleSuccess);
      document.removeEventListener("AppleIDSignInOnFailure", onAppleFailure);
    };
  }, []);

  // Social Login Mutation
  const { mutate: socialLoginMutate, isPending: isSocialLoginPending } = useMutation({
    mutationFn: socialLoginAPI,
    onSuccess: (data) => {

      // Store token in localStorage
      if (data?.data?.token) {
        setAuthToken(data?.data?.token, true);
        localStorage.setItem("token", data?.data?.token || "");
      }

      // Store user data in Redux
      if (data?.data?.user) {
        dispatch(setUser(data.data.user as any));
      }

      // Try to register fcm token if available after token is saved
      const fcmToken = localStorage.getItem("fcmToken");
      if (fcmToken) {
        updateDeviceTokenAPI(fcmToken, "web").catch(err => console.log("FCM registration error after login:", err));
      }

      useToast(t("messages.loginSuccess"), "success");
      dispatch(closeModal());
      setIsAppleLoading(false);
      navigate("/create-game");
    },
    onError: (error: Error) => {
      console.error("Google Login Error:", error.message);
      useToast(error.message || t("messages.loginFailed"), "error");
      setIsAppleLoading(false);
    },
  });

  const [isAppleLoading, setIsAppleLoading] = useState(false);

  const onAppleSuccess = (event: any) => {
    const response = event.detail;
    const { authorization, user } = response;

    // Decode ID token to get sub (unique social_id) and email
    const decodedToken = decodeAppleToken(authorization.id_token);
    const appleEmail = user?.email || decodedToken?.email || "";
    const appleName = user ? `${user.name.firstName} ${user.name.lastName}` : decodedToken?.name || "";
    const socialId = decodedToken?.sub || authorization.id_token;

    socialLoginMutate({
      email: appleEmail,
      social_type: "apple",
      full_name: appleName,
      social_id: socialId,
    });
  };

  const onAppleFailure = (error: any) => {
    console.error("Apple login failed", error);
    useToast(t("auth.signIn.appleLoginFailed"), "error");
  };

  const handleAppleLogin = () => {
    requestNotificationPermissionOnGesture();
    const apple = (window as any).AppleID;

    if (!apple) {
      useToast(t("auth.signIn.appleLoginNotAvailable"), "error");
      return;
    }

    apple.auth.signIn();
  };

  const [isFacebookLoading, setIsFacebookLoading] = useState(false);
  const handleFacebookLogin = async () => {
    try {
      requestNotificationPermissionOnGesture();
      setIsFacebookLoading(true);
      if (!window.FB) {
        await loadFacebookSDK(import.meta.env.VITE_FACEBOOK_APP_ID);
      }

      if (!window.FB) {
        useToast(t("login.facebookLoginFailed"), "error");
        return;
      }

      const statusResponse = await getFacebookLoginStatus();

      if (statusResponse.status === "connected") {
        fetchFacebookUserData();
      } else {
        window.FB.login(
          (response: any) => {
            if (response.authResponse) {
              fetchFacebookUserData();
            } else {
              useToast(t("login.facebookLoginCancelled"), "error");
            }
          },
          {
            scope: "public_profile,email",
          }
        );
      }
    } catch (error) {
      console.error("Facebook login error:", error);
      useToast(t("login.facebookLoginFailed"), "error");
      setIsFacebookLoading(false);
    }
  };

  const fetchFacebookUserData = () => {
    if (!window.FB) return;

    window.FB.api("/me", { fields: "id,name,email,picture" }, (userInfo: any) => {
      if (userInfo && !userInfo.error) {
        socialLoginMutate({
          email: userInfo.email ?? "",
          social_type: "facebook",
          social_id: userInfo.id,
          full_name: userInfo.name ?? "",
        });
        setIsFacebookLoading(false);
      } else {
        console.error("Facebook API error:", userInfo.error);
        useToast(t("login.facebookLoginFailed"), "error");
        setIsFacebookLoading(false);
      }
    });
  };



  return (
    <ModalWrapper onClose={handleClose}>
      <div className="text-center lg:mb-[1vw] mb-3">
        <h1 className="lg:text-[1.9vw]/[2.2vw] text-2xl/6 font-semibold text-primary-orange lg:mb-[0.3vw] mb-2">
          {t("register.registerYourAccount")}
        </h1>
        <p className="lg:text-[1.1vw]/[1.4vw] text-base/tight text-black-800 font-bold">
          {t("register.signUpQuickly")}
        </p>
      </div>

      <div className="flex lg:mb-[1.1vw] md:mb-5 mb-4 lg:p-[1%] p-1.5 bg-primary-ivory skew-x-[-10deg] 2xl:rounded-[0.26vw] rounded-[5px] relative">
        <div
          className={`absolute lg:top-[0.3vw] top-1.5 lg:bottom-[0.3vw] bottom-1.5 2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] transition-all duration-300 ease-in-out bg-purple-fill bg-[#FFFBA1] ${authMode === "mobile"
            ? "lg:start-[0.4vw] start-1.5 lg:w-[calc(50%-0.4vw)] w-[calc(50%-12px)] border-primary-rose"
            : "lg:start-[calc(50%+0.4vw)] start-[calc(50%+6px)] lg:w-[calc(50%-0.9vw)] w-[calc(50%-12px)]"
            }`}
        ></div>
        <button
          type="button"
          onClick={() => setAuthMode("mobile")}
          className={`flex-1 2xl:py-[0.65vw] py-2 2xl:mt-[0.208vw] mt-1 lg:px-[0.6vw] px-6 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[10deg] font-bold lg:text-[1.1vw]/none md:text-lg/none text-base/none transition-all duration-200 text-center relative z-10  cursor-pointer focus:outline-none ${authMode === "mobile" ? "text-primary-rose border-primary-rose" : "text-black-300 border-transparent"
            }`}
        >
          {t("register.mobile")}
        </button>
        <button
          type="button"
          onClick={() => setAuthMode("email")}
          className={`flex-1 2xl:py-[0.65vw] py-2 2xl:mt-[0.208vw] mt-1 lg:px-[0.6vw] px-6 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[10deg] font-bold lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight transition-all duration-200 text-center relative z-10 cursor-pointer focus:outline-none ${authMode === "email" ? "text-primary-rose border-primary-rose" : "text-black-300 border-transparent"
            }`}
        >
          {t("register.email")}
        </button>
      </div>

      {authMode === "mobile" ? <MobileRegisterForm /> : <EmailRegisterForm />}

      <div className="flex items-center lg:gap-[1.481vw] md:gap-4 gap-3 lg:mb-[0.9vw] mb-4">
        <div className="bg-[linear-gradient(90deg,#7364FF_0%,rgba(11,51,105,0)_100%)] 2xl:h-[0.052vw] h-[1px] w-full 2xl:max-w-[7.813vw] max-w-[150px] -scale-x-100"></div>
        <span className="text-black-900 lg:text-[1vw]/[1.2vw] md:text-base/tight text-sm/tight font-bold whitespace-nowrap">
          {t("register.orContinueWith")}
        </span>
        <div className="bg-[linear-gradient(90deg,#7364FF_0%,rgba(11,51,105,0)_100%)] 2xl:h-[0.052vw] h-[1px] w-full 2xl:max-w-[7.813vw] max-w-[150px] rtl:-scale-x-100"></div>
      </div>

      <div className="flex lg:gap-[0.7vw] gap-1.5 justify-center">
        <button
          onClick={handleGoogleLogin}
          type="button"
          disabled={isGoogleLoading}
          className="flex flex-1 lg:gap-[0.5vw] gap-2 items-center 2xl:py-[0.65vw] py-2.5 lg:px-[4%] md:px-3.5 px-2.5 2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] justify-center
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] text-xl/tight font-semibold bg-white text-black-300 cursor-pointer transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGoogleLoading ? (
            <div className="flex items-center justify-center">
              <div className="w-6 h-6 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <svg
              className="lg:w-[1.3vw] w-5 lg:h-[1.3vw] h-5 skew-x-[10deg]"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_386_23)">
                <path
                  d="M7.09188 19.338L5.978 23.4962L1.90681 23.5823C0.690125 21.3257 0 18.7437 0 16C0 13.3468 0.64525 10.8448 1.789 8.64172H1.78988L5.41437 9.30622L7.00212 12.909C6.66981 13.8778 6.48869 14.9178 6.48869 16C6.48881 17.1745 6.70156 18.2998 7.09188 19.338Z"
                  fill="#FBBB00"
                />
                <path
                  d="M31.7203 13.011C31.904 13.9789 31.9998 14.9784 31.9998 16C31.9998 17.1455 31.8794 18.2629 31.6499 19.3407C30.8711 23.0084 28.8359 26.211 26.0166 28.4774L26.0157 28.4765L21.4504 28.2435L20.8043 24.2101C22.6751 23.113 24.1371 21.396 24.9072 19.3407H16.3516V13.011H25.032H31.7203Z"
                  fill="#518EF8"
                />
                <path
                  d="M26.0157 28.4765L26.0166 28.4774C23.2747 30.6813 19.7915 32 15.9999 32C9.90674 32 4.60918 28.5943 1.90674 23.5824L7.0918 19.3381C8.44299 22.9442 11.9217 25.5113 15.9999 25.5113C17.7529 25.5113 19.3951 25.0374 20.8043 24.2101L26.0157 28.4765Z"
                  fill="#28A745"
                />
                <path
                  d="M26.2128 3.6835L21.0295 7.927C19.5711 7.01538 17.8471 6.48875 16.0001 6.48875C11.8295 6.48875 8.28575 9.17356 7.00225 12.909L1.78994 8.64175H1.78906C4.45194 3.50769 9.81631 0 16.0001 0C19.8823 0 23.4418 1.38287 26.2128 3.6835Z"
                  fill="#F14336"
                />
              </g>
              <defs>
                <clipPath id="clip0_386_23">
                  <rect width="32" height="32" fill="white" />
                </clipPath>
              </defs>
            </svg>
          )}
          {/* <span className="md:block hidden">{t("register.google")}</span> */}
        </button>

        <button
          type="button"
          // onClick={handleFacebookLogin}
          onClick={handleCommingSoon}
          disabled={isFacebookLoading}
          className="flex flex-1 lg:gap-[0.5vw] gap-2 items-center 2xl:py-[0.65vw] py-2.5 lg:px-[4%] md:px-3.5 px-2.5 2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] justify-center
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] text-xl/tight font-semibold bg-white text-black-300 cursor-pointer transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {
            isFacebookLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-6 h-6 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <svg
                className="lg:w-[1.3vw] w-5 lg:h-[1.3vw] h-5 skew-x-[10deg]"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_386_30)">
                  <path
                    d="M13.36 31.84C5.76 30.48 0 23.92 0 16C0 7.2 7.2 0 16 0C24.8 0 32 7.2 32 16C32 23.92 26.24 30.48 18.64 31.84L17.76 31.12H14.24L13.36 31.84Z"
                    fill="url(#paint0_linear_386_30)"
                  />
                  <path
                    d="M22.2399 20.48L22.9599 16H18.7199V12.88C18.7199 11.6 19.1999 10.64 21.1199 10.64H23.1999V6.55999C22.0799 6.39999 20.7999 6.23999 19.6799 6.23999C15.9999 6.23999 13.4399 8.47999 13.4399 12.48V16H9.43994V20.48H13.4399V31.76C14.3199 31.92 15.1999 32 16.0799 32C16.9599 32 17.8399 31.92 18.7199 31.76V20.48H22.2399Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <linearGradient
                    id="paint0_linear_386_30"
                    x1="16.0008"
                    y1="30.8871"
                    x2="16.0008"
                    y2="-0.00589422"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#0062E0" />
                    <stop offset="1" stopColor="#19AFFF" />
                  </linearGradient>
                  <clipPath id="clip0_386_30">
                    <rect width="32" height="32" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            )
          }

          {/* <span className="md:block hidden">{t("register.facebook")}</span> */}
        </button>

        <button
          type="button"
          onClick={handleAppleLogin}
          disabled={isAppleLoading}
          className="flex flex-1 lg:gap-[0.5vw] gap-2 items-center 2xl:py-[0.65vw] py-2.5 lg:px-[4%] md:px-3.5 px-2.5 2xl:rounded-[0.26vw] rounded-[5px]
    skew-x-[-10deg] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] justify-center
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] text-xl/tight font-semibold bg-white text-black-300 cursor-pointer transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isAppleLoading ? (<div className="flex items-center justify-center">
            <div className="w-6 h-6 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
          </div>) : (<svg
            className="lg:w-[1.3vw] w-5 lg:h-[1.3vw] h-5 skew-x-[10deg]"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_386_35)">
              <path
                d="M21.9988 0C20.2928 0.117999 18.2988 1.20999 17.1369 2.63198C16.0769 3.92198 15.2049 5.83797 15.5449 7.69995C17.4088 7.75795 19.3348 6.63996 20.4508 5.19397C21.4948 3.84798 22.2848 1.94399 21.9988 0Z"
                fill="#1A201F"
              />
              <path
                d="M28.7406 10.7362C27.1026 8.68223 24.8006 7.49023 22.6266 7.49023C19.7566 7.49023 18.5427 8.86423 16.5487 8.86423C14.4927 8.86423 12.9307 7.49423 10.4487 7.49423C8.01071 7.49423 5.41473 8.98423 3.76874 11.5322C1.45475 15.1202 1.85075 21.8661 5.60073 27.6121C6.94272 29.6681 8.73471 31.9801 11.0787 32.0001C13.1647 32.0201 13.7527 30.6621 16.5787 30.6481C19.4046 30.6321 19.9406 32.0181 22.0226 31.9961C24.3686 31.9781 26.2586 29.4161 27.6006 27.3601C28.5626 25.8861 28.9206 25.1441 29.6666 23.4801C24.2406 21.4142 23.3706 13.6982 28.7406 10.7362Z"
                fill="#1A201F"
              />
            </g>
            <defs>
              <clipPath id="clip0_386_35">
                <rect width="32" height="32" fill="white" />
              </clipPath>
            </defs>
          </svg>)
          }

          {/* <span className="md:block hidden">{t("register.apple")}</span> */}
        </button>
      </div>
      {/* Already have an account */}
      <div className="text-center lg:mt-[1.1vw] mt-4">
        <span className="lg:text-[1.1vw]/[1.3vw] text-base/tight text-black-800 font-bold">
          {t("register.alreadyHaveAccount")}{" "}
        </span>
        <button
          type="button"
          onClick={() => dispatch(setModalType("login"))}
          className="lg:text-[1.1vw]/[1.3vw] text-base/tight font-bold text-primary-orange hover:underline cursor-pointer"
        >
          {t("register.login")}
        </button>
      </div>
    </ModalWrapper>
  );
};

export default RegisterModal;
